import { Button, Modal } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { CourseTime } from '../../components/CourseTime';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { newClientToken } from '../../lib/client-token';
import { ApiError } from '../../lib/http';
import { CANCEL_RULE, canSelfCancel, enrollmentLabel } from '../../lib/status';
import { coursesApi } from '../../services/resources';
import { getUser } from '../../stores/session';
import type { Course, Enrollment } from '../../types/api';

export function KioskEnrollmentsPage() {
  const [rows, setRows] = useState<Enrollment[]>([]);
  const [error, setError] = useState('');
  const me = getUser();

  const load = useCallback(async () => {
    try {
      const courses = await coursesApi.list({ page: 1, limit: 50 });
      const all: Enrollment[] = [];
      for (const c of courses.items) {
        const page = await coursesApi.enrollments(c.id);
        for (const e of page.items) {
          if (e.user_id === me?.id) all.push({ ...e, course: e.course ?? c });
        }
      }
      setRows(all);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '报名记录失败');
    }
  }, [me?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <EmptyState text={error} />;
  if (rows.length === 0) return <EmptyState text="你还没有报名记录。" />;

  return (
    <div className="stack">
      {rows.map((e) => (
        <EnrollmentCard key={e.id} row={e} onChanged={() => void load()} />
      ))}
    </div>
  );
}

function EnrollmentCard(props: { row: Enrollment; onChanged: () => void }) {
  const course = props.row.course as Course | undefined;
  const [open, setOpen] = useState(false);
  const allow = course ? canSelfCancel(course.starts_at) : false;

  async function cancel() {
    if (!course) return;
    await coursesApi.cancelEnrollment(course.id, props.row.id);
    setOpen(false);
    props.onChanged();
  }

  async function reenroll() {
    if (!course) return;
    await coursesApi.enroll(course.id, newClientToken());
    props.onChanged();
  }

  return (
    <article className="surface" style={{ padding: 24 }}>
      <h2>{course?.title ?? '开放课'}</h2>
      {course ? <CourseTime course={course} /> : null}
      <StatusBadge status={props.row.status} label={enrollmentLabel(props.row.status)} />
      <p className="meta">{CANCEL_RULE}</p>
      <div className="row" style={{ gap: 16, marginTop: 16 }}>
        {props.row.status !== 'cancelled' ? (
          <Button danger className="kiosk-primary" disabled={!allow} onClick={() => setOpen(true)}>
            取消
          </Button>
        ) : (
          <Button type="primary" className="kiosk-primary" onClick={() => void reenroll()}>
            重新报名
          </Button>
        )}
      </div>
      <Modal open={open} footer={null} onCancel={() => setOpen(false)} title="确认取消报名？">
        <div className="row" style={{ gap: 24 }}>
          <Button className="kiosk-secondary" onClick={() => setOpen(false)}>
            返回
          </Button>
          <Button danger className="kiosk-primary" onClick={() => void cancel()}>
            确认取消
          </Button>
        </div>
      </Modal>
    </article>
  );
}
